/**
 * ANOTAÇÃO DE IMAGEM — modelo e regras puras do editor de fotos/croquis.
 *
 * Duas decisões estruturais sustentam todo o resto:
 *
 * 1. COORDENADAS NORMALIZADAS (0..1). Uma seta não é gravada em pixels da
 *    tela em que foi desenhada, e sim como fração da largura/altura da
 *    imagem. Assim a mesma anotação vale no editor, na miniatura, no zoom e
 *    no PDF — sem recalcular nada e sem "escorregar" quando a imagem é
 *    exibida em outro tamanho.
 *
 * 2. ANOTAÇÃO É DADO, NÃO PIXEL (comando, item 17). O que se grava é a lista
 *    de objetos; a imagem achatada é um SUBPRODUTO para uso documental
 *    (item 18). Por isso é possível reabrir e editar uma marcação feita
 *    ontem — e por isso a imagem original nunca precisa ser sobrescrita.
 */

/** Paleta fixa (item 8) — nome oficial na UI e a cor real de desenho. */
export const ANNOTATION_COLORS = {
  VERMELHO: "#DC2626",
  AMARELO: "#FACC15",
  AZUL: "#2563EB",
  BRANCO: "#FFFFFF",
  PRETO: "#000000"
} as const;
export type AnnotationColorName = keyof typeof ANNOTATION_COLORS;
export const DEFAULT_COLOR: AnnotationColorName = "VERMELHO";

/**
 * Espessura (item 9) em fração da menor dimensão da imagem: uma linha
 * "MÉDIA" precisa parecer média tanto numa foto de 800px quanto numa de
 * 4000px — em pixels fixos ela sumiria na foto grande.
 */
export const STROKE_WIDTHS = { FINA: 0.002, MÉDIA: 0.004, GROSSA: 0.008 } as const;
export type StrokeWidthName = keyof typeof STROKE_WIDTHS;
export const DEFAULT_STROKE: StrokeWidthName = "MÉDIA";

/** Tamanho do texto (item 10), também em fração da altura da imagem. */
export const FONT_SIZES = { PEQUENO: 0.03, MÉDIO: 0.05, GRANDE: 0.08 } as const;
export type FontSizeName = keyof typeof FONT_SIZES;
export const DEFAULT_FONT_SIZE: FontSizeName = "MÉDIO";

interface AnnotationBase {
  readonly id: string;
  readonly color: AnnotationColorName;
}

/** Seta: ponto inicial → ponto final, a direção é do primeiro para o segundo. */
export interface ArrowAnnotation extends AnnotationBase {
  readonly type: "arrow";
  readonly x1: number; readonly y1: number;
  readonly x2: number; readonly y2: number;
  readonly strokeWidth: StrokeWidthName;
}

/** Elipse/círculo — só contorno, nunca preenchida (item 5). */
export interface EllipseAnnotation extends AnnotationBase {
  readonly type: "ellipse";
  readonly x: number; readonly y: number;
  readonly width: number; readonly height: number;
  readonly strokeWidth: StrokeWidthName;
}

/** Retângulo/quadrado — só contorno (item 6). */
export interface RectAnnotation extends AnnotationBase {
  readonly type: "rect";
  readonly x: number; readonly y: number;
  readonly width: number; readonly height: number;
  readonly strokeWidth: StrokeWidthName;
}

export interface TextAnnotation extends AnnotationBase {
  readonly type: "text";
  readonly x: number; readonly y: number;
  readonly text: string;
  readonly fontSize: FontSizeName;
}

export type ImageAnnotation = ArrowAnnotation | EllipseAnnotation | RectAnnotation | TextAnnotation;
export type AnnotationTool = ImageAnnotation["type"];

export class AnnotationRuleError extends Error {}

/**
 * Formatos aceitos (item 28) — os mesmos que os builders de PDF conseguem
 * embutir. A imagem trafega como data URL porque é o formato que o
 * `DocumentStorageService` guarda e que o pdfmake consome; um caminho de
 * arquivo local ou um blob de outro tipo quebraria o documento na geração.
 */
export function isEmbeddableImageDataUrl(value: unknown): boolean {
  return typeof value === "string" && /^data:image\/(png|jpe?g|webp);base64,/i.test(value);
}

/** Menor tamanho aceito para uma forma: abaixo disso é clique acidental, não marcação. */
const MIN_SHAPE_SIZE = 0.01;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

function requireFinite(value: unknown, field: string): number {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) throw new AnnotationRuleError(`Coordenada inválida em "${field}".`);
  return numeric;
}

/**
 * Normaliza a anotação recebida da tela: prende as coordenadas ao quadro da
 * imagem (nada fora dela), reordena retângulo/elipse desenhados "de trás
 * para frente" e recusa forma degenerada ou texto vazio.
 */
export function normalizeAnnotation(annotation: ImageAnnotation): ImageAnnotation {
  if (annotation.type === "arrow") {
    const x1 = clamp01(requireFinite(annotation.x1, "x1"));
    const y1 = clamp01(requireFinite(annotation.y1, "y1"));
    const x2 = clamp01(requireFinite(annotation.x2, "x2"));
    const y2 = clamp01(requireFinite(annotation.y2, "y2"));
    if (Math.hypot(x2 - x1, y2 - y1) < MIN_SHAPE_SIZE) {
      throw new AnnotationRuleError("Arraste para definir o tamanho e a direção da seta.");
    }
    return { ...annotation, x1, y1, x2, y2 };
  }

  if (annotation.type === "text") {
    const text = String(annotation.text ?? "").trim();
    if (text === "") throw new AnnotationRuleError("Digite o texto da marcação.");
    return {
      ...annotation,
      x: clamp01(requireFinite(annotation.x, "x")),
      y: clamp01(requireFinite(annotation.y, "y")),
      text
    };
  }

  // Retângulo e elipse: desenhar da direita para a esquerda produz largura
  // negativa — vira origem + tamanho positivo antes de gravar.
  let x = requireFinite(annotation.x, "x");
  let y = requireFinite(annotation.y, "y");
  let width = requireFinite(annotation.width, "width");
  let height = requireFinite(annotation.height, "height");
  if (width < 0) { x += width; width = -width; }
  if (height < 0) { y += height; height = -height; }
  x = clamp01(x);
  y = clamp01(y);
  width = Math.min(width, 1 - x);
  height = Math.min(height, 1 - y);
  if (width < MIN_SHAPE_SIZE || height < MIN_SHAPE_SIZE) {
    throw new AnnotationRuleError("Arraste para definir o tamanho da marcação.");
  }
  return { ...annotation, x, y, width, height };
}

/** Caixa envolvente normalizada — usada para seleção, handles e hit-test. */
export function boundsOf(annotation: ImageAnnotation): { x: number; y: number; width: number; height: number } {
  if (annotation.type === "arrow") {
    return {
      x: Math.min(annotation.x1, annotation.x2),
      y: Math.min(annotation.y1, annotation.y2),
      width: Math.abs(annotation.x2 - annotation.x1),
      height: Math.abs(annotation.y2 - annotation.y1)
    };
  }
  if (annotation.type === "text") {
    // O texto não tem caixa própria no modelo: aproxima-se pela altura da
    // fonte e por uma largura média de caractere, suficiente para
    // selecionar e arrastar.
    const height = FONT_SIZES[annotation.fontSize];
    return { x: annotation.x, y: annotation.y - height, width: Math.min(1, annotation.text.length * height * 0.55), height };
  }
  return { x: annotation.x, y: annotation.y, width: annotation.width, height: annotation.height };
}

/**
 * Qual anotação está sob o ponto clicado. Percorre de trás para frente
 * porque a última desenhada é a que está por cima — é ela que o usuário
 * espera pegar.
 */
export function annotationAt(
  annotations: readonly ImageAnnotation[],
  point: { x: number; y: number },
  tolerance = 0.01
): ImageAnnotation | null {
  for (let index = annotations.length - 1; index >= 0; index--) {
    const annotation = annotations[index]!;
    const bounds = boundsOf(annotation);
    if (
      point.x >= bounds.x - tolerance && point.x <= bounds.x + bounds.width + tolerance &&
      point.y >= bounds.y - tolerance && point.y <= bounds.y + bounds.height + tolerance
    ) {
      return annotation;
    }
  }
  return null;
}

/** Move uma anotação por um deslocamento, sem deixá-la sair da imagem. */
export function moveAnnotation(annotation: ImageAnnotation, dx: number, dy: number): ImageAnnotation {
  if (annotation.type === "arrow") {
    return {
      ...annotation,
      x1: clamp01(annotation.x1 + dx), y1: clamp01(annotation.y1 + dy),
      x2: clamp01(annotation.x2 + dx), y2: clamp01(annotation.y2 + dy)
    };
  }
  if (annotation.type === "text") {
    return { ...annotation, x: clamp01(annotation.x + dx), y: clamp01(annotation.y + dy) };
  }
  return {
    ...annotation,
    x: Math.min(clamp01(annotation.x + dx), 1 - annotation.width),
    y: Math.min(clamp01(annotation.y + dy), 1 - annotation.height)
  };
}

/**
 * VERSÃO ANOTADA de uma imagem (itens 3/19/20). `originalFileId` nunca muda;
 * `annotatedFileId` aponta para a imagem achatada gerada a partir das
 * anotações. Sem anotação nenhuma, `annotatedFileId` é null e o documento
 * usa a original.
 */
export interface AnnotatedImage {
  readonly originalFileId: string;
  readonly annotatedFileId: string | null;
  readonly annotations: readonly ImageAnnotation[];
  readonly updatedBy: string | null;
  readonly updatedAt: string | null;
}

/** Qual arquivo o documento deve usar: a anotada quando existir (item 19). */
export function effectiveFileIdOf(image: Pick<AnnotatedImage, "originalFileId" | "annotatedFileId">): string {
  return image.annotatedFileId ?? image.originalFileId;
}

export function isAnnotated(image: Pick<AnnotatedImage, "annotatedFileId" | "annotations">): boolean {
  return image.annotatedFileId !== null && image.annotations.length > 0;
}

/**
 * RESTAURAR ORIGINAL (item 20): o documento volta a usar a imagem original.
 * As anotações são descartadas do estado corrente, mas o arquivo anotado
 * anterior não é apagado — o histórico de quem já usou aquela versão
 * (snapshots de revisões fechadas) continua íntegro.
 */
export function restoreOriginal(image: AnnotatedImage): AnnotatedImage {
  return { ...image, annotatedFileId: null, annotations: [] };
}

// ------------------------------------------------------- desfazer / refazer

/**
 * Histórico do editor (item 12). Estrutura clássica passado/presente/futuro:
 * cada alteração confirmada empurra o estado atual para o passado e zera o
 * futuro — refazer só existe enquanto o usuário não cria um caminho novo.
 */
export interface AnnotationHistory {
  readonly past: ReadonlyArray<readonly ImageAnnotation[]>;
  readonly present: readonly ImageAnnotation[];
  readonly future: ReadonlyArray<readonly ImageAnnotation[]>;
}

/** Limite de passos guardados: o suficiente para o uso real, sem crescer sem fim. */
const MAX_HISTORY = 50;

export function historyOf(annotations: readonly ImageAnnotation[] = []): AnnotationHistory {
  return { past: [], present: annotations, future: [] };
}

export function commitAnnotations(history: AnnotationHistory, next: readonly ImageAnnotation[]): AnnotationHistory {
  const past = [...history.past, history.present].slice(-MAX_HISTORY);
  return { past, present: next, future: [] };
}

export function canUndo(history: AnnotationHistory): boolean {
  return history.past.length > 0;
}
export function canRedo(history: AnnotationHistory): boolean {
  return history.future.length > 0;
}

export function undo(history: AnnotationHistory): AnnotationHistory {
  if (!canUndo(history)) return history;
  const previous = history.past[history.past.length - 1]!;
  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future]
  };
}

export function redo(history: AnnotationHistory): AnnotationHistory {
  if (!canRedo(history)) return history;
  const next = history.future[0]!;
  return {
    past: [...history.past, history.present],
    present: next,
    future: history.future.slice(1)
  };
}

/** LIMPAR MARCAÇÕES (item 14) — some com as anotações, nunca com a imagem. */
export function clearAnnotations(history: AnnotationHistory): AnnotationHistory {
  return commitAnnotations(history, []);
}
